// frontend/src/components/ApiKeysManager/ApiKeysManager.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { 
    Box, Button, Typography, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, 
    DialogContent, DialogContentText, DialogActions, TextField, 
    IconButton, Tooltip, CircularProgress, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { AuthContext } from '../../contexts/AuthContext';


const ApiKeysManager = () => {
    const { t, i18n } = useTranslation();
    const { fetchWithAuth } = useContext(AuthContext);
    const [keys, setKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Create Key State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    
    // Display Raw Key State
    const [newRawKey, setNewRawKey] = useState('');
    const [isRawKeyModalOpen, setIsRawKeyModalOpen] = useState(false);
    const [isCopied, setIsCopied] = useState(false); // State for copy feedback

    // --- NEW: Revoke Key Confirmation Modal State ---
    const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
    const [keyToRevoke, setKeyToRevoke] = useState(null);
    const [isRevoking, setIsRevoking] = useState(false);

    const formatApiDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    };

    const fetchKeys = async () => {
        setIsLoading(true);
        try {
            const res = await fetchWithAuth('/api/user/api-keys');
            if (res.ok) {
                const data = await res.json();
                setKeys(data.keys);
            } else {
                throw new Error(t('accountPage.apiKeysManager.loadingError'));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateKey = async () => {
        if (!newKeyName.trim()) return;
        setIsCreating(true);
        setError('');
        try {
            const res = await fetchWithAuth('/api/user/api-keys', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newKeyName })
            });
            const data = await res.json();
            if (res.ok) {
                setKeys([data.key_record, ...keys]);
                setNewRawKey(data.raw_key);
                setIsCreateModalOpen(false);
                setNewKeyName('');
                setIsRawKeyModalOpen(true); // Open the ONE-TIME display modal
            } else {
                throw new Error(data.message || t('accountPage.apiKeysManager.createError'));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCreating(false);
        }
    };

    const openRevokeDialog = (key) => {
        setKeyToRevoke(key);
        setIsRevokeModalOpen(true);
    };

    const closeRevokeDialog = () => {
        setKeyToRevoke(null);
        setIsRevokeModalOpen(false);
    };

    const confirmRevokeKey = async () => {
        if (!keyToRevoke) return;
        setIsRevoking(true);
        try {
            const res = await fetchWithAuth(`/api/user/api-keys/${keyToRevoke.id}`, { method: 'DELETE' });
            if (res.ok) {
                setKeys(keys.filter(k => k.id !== keyToRevoke.id));
                closeRevokeDialog();
            } else {
                const data = await res.json();
                setError(data.message || t('accountPage.apiKeysManager.revokeError'));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsRevoking(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(newRawKey);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2500);
    };

    // Shared styles for table cells
    const cellStyle = {
        color: 'rgba(255, 255, 255, 0.85)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
    };
    
    const headerCellStyle = {
        ...cellStyle,
        color: 'white',
        fontWeight: 600,
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
    };

    // --- SHARED MODAL STYLES (Matches VerificationModal.css) ---
    const glassDialogProps = {
        sx: {
            backgroundColor: 'var(--primary-color, #0f172a)', // Matches main app dark theme
            backgroundImage: 'none', // Removes default MUI elevation gradient
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            borderRadius: '16px',
            color: '#f1f5f9',
        }
    };

    const glassBackdropProps = {
        sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
        }
    };

    const primaryButtonSx = {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '1rem',
        padding: '10px 24px',
        borderRadius: '8px',
        backgroundColor: 'var(--secondary-color) !important',
        color: 'white !important',
        border: '1px solid transparent !important',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'var(--btn-hover-color) !important',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
            color: 'rgba(255, 255, 255, 0.3) !important',
            boxShadow: 'none',
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header matches Account Details section */}
            <div className="account-section-header" style={{ marginBottom: '0.5rem', paddingBottom: '1rem' }}>
                <h2 style={{ paddingBottom: 0 }}>{t('accountPage.apiKeysManager.headerTitle')}</h2>
                <Button 
                    variant="outlined" 
                    className="edit-button" 
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    {t('accountPage.apiKeysManager.buttons.create')}
                </Button>
            </div>

            <Typography variant="body2" className="setting-description" sx={{ pl: 0, mb: 3 }}>
                {t('accountPage.apiKeysManager.description')}
            </Typography>

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.2)', color: '#ffb4ab', border: '1px solid rgba(211, 47, 47, 0.5)' }} 
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: 'var(--secondary-color)' }} />
                </Box>
            ) : (
                <TableContainer 
                    sx={{ 
                        backgroundColor: 'transparent', 
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '8px'
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyle}>{t('accountPage.apiKeysManager.table.name')}</TableCell>
                                <TableCell sx={headerCellStyle}>{t('accountPage.apiKeysManager.table.prefix')}</TableCell>
                                <TableCell sx={headerCellStyle}>{t('accountPage.apiKeysManager.table.created')}</TableCell>
                                <TableCell sx={headerCellStyle}>{t('accountPage.apiKeysManager.table.lastUsed')}</TableCell>
                                <TableCell align="right" sx={headerCellStyle}>{t('accountPage.apiKeysManager.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {keys.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'rgba(255, 255, 255, 0.5)', borderBottom: 'none' }}>
                                        {t('accountPage.apiKeysManager.table.empty')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                keys.map((key) => (
                                    <TableRow key={key.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={cellStyle}>{key.name}</TableCell>
                                        <TableCell sx={cellStyle}>
                                            <code style={{ 
                                                backgroundColor: 'rgba(0, 0, 0, 0.3)', 
                                                color: 'var(--secondary-color)', 
                                                padding: '4px 8px', 
                                                borderRadius: '4px',
                                                border: '1px solid rgba(255, 255, 255, 0.1)'
                                            }}>
                                                {key.key_prefix}...
                                            </code>
                                        </TableCell>
                                        <TableCell sx={{ ...cellStyle, color: 'rgba(255, 255, 255, 0.6)' }}>
                                            {formatApiDate(key.created_at)}
                                        </TableCell>
                                        <TableCell sx={{ ...cellStyle, color: 'rgba(255, 255, 255, 0.6)' }}>
                                            {key.last_used_at 
                                                ? formatApiDate(key.last_used_at) 
                                                : t('accountPage.apiKeysManager.table.never')
                                            }
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                            <Tooltip title={t('accountPage.apiKeysManager.tooltips.revoke')}>
                                                <IconButton sx={{ color: '#e57373' }} onClick={() => openRevokeDialog(key)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* --- UPDATED: Create Key Modal --- */}
            <Dialog 
                open={isCreateModalOpen} 
                onClose={() => setIsCreateModalOpen(false)} 
                PaperProps={glassDialogProps}
                slotProps={{ backdrop: glassBackdropProps }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        pb: 2,
                        mb: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                    }}
                    >
                    {t('accountPage.apiKeysManager.dialogs.create.title')}
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <DialogContentText sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('accountPage.apiKeysManager.dialogs.create.description')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('accountPage.apiKeysManager.dialogs.create.label')}
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        variant="filled"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                            '& .MuiInputBase-input': { color: 'white' },
                            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--secondary-color)' }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button 
                        onClick={() => setIsCreateModalOpen(false)} 
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', textTransform: 'none', fontWeight: 600 }}
                    >
                        {t('accountPage.apiKeysManager.dialogs.create.cancel')}
                    </Button>
                    <Button 
                        onClick={handleCreateKey} 
                        variant="contained" 
                        sx={primaryButtonSx} 
                        disabled={!newKeyName.trim() || isCreating}
                    >
                        {isCreating ? t('accountPage.apiKeysManager.dialogs.create.creating') : t('accountPage.apiKeysManager.dialogs.create.submit')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Display Raw Key Modal --- */}
            <Dialog 
                open={isRawKeyModalOpen} 
                onClose={() => setIsRawKeyModalOpen(false)} 
                maxWidth="sm" 
                fullWidth 
                PaperProps={glassDialogProps}
                slotProps={{ backdrop: glassBackdropProps }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', pb: 2, mb: 2, fontSize: '1.1rem', fontWeight: 600, color: '#81c784' }}>
                    {t('accountPage.apiKeysManager.dialogs.display.title')}
                </DialogTitle>

                <DialogContent sx={{ pb: 1 }}>
                    <Alert 
                        severity="warning" 
                        sx={{ 
                            mb: 3, 
                            bgcolor: 'rgba(255, 152, 0, 0.1)', 
                            color: '#ffb74d', 
                            border: '1px solid rgba(255, 152, 0, 0.3)',
                            alignItems: 'center',
                            '& .MuiAlert-icon': { mt: 0 },
                        }}>
                        <Trans i18nKey="accountPage.apiKeysManager.dialogs.display.warning" components={[<strong />]} />
                    </Alert>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        backgroundColor: 'rgba(0, 0, 0, 0.4)', 
                        p: 1.5, 
                        borderRadius: 2,
                        gap: 2,
                    }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all', color: 'white', pl: 1 }}>
                            {newRawKey}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={copyToClipboard}
                            startIcon={isCopied ? <CheckIcon /> : <ContentCopyIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: isCopied ? 'var(--success-color, #69f0ae)' : 'var(--secondary-color)',
                                color: isCopied ? 'var(--success-color, #69f0ae)' : 'var(--secondary-color)',
                                '&:hover': {
                                    borderColor: isCopied ? 'var(--success-color, #69f0ae)' : 'var(--secondary-color)',
                                    backgroundColor: isCopied ? 'rgba(105, 240, 174, 0.1)' : 'rgba(79, 195, 247, 0.1)'
                                },
                                flexShrink: 0, // Prevent button from shrinking
                            }}
                        >
                            {isCopied ? t('contactPage.copyFeedback') : t('accountPage.apiKeysManager.tooltips.copy')}
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'center' }}>
                    <Button 
                        onClick={() => setIsRawKeyModalOpen(false)} 
                        variant="contained" 
                        sx={{ ...primaryButtonSx, width: '100%' }}
                    >
                        {t('accountPage.apiKeysManager.dialogs.display.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- NEW: Revoke Confirmation Modal --- */}
            <Dialog
                open={isRevokeModalOpen}
                onClose={closeRevokeDialog}
                PaperProps={glassDialogProps}
                slotProps={{ backdrop: glassBackdropProps }}
                maxWidth="xs"
            >
                <DialogTitle sx={{fontSize: '1.1rem', fontWeight: 600}}>
                    {/* Add translation for this later */}
                    Revoke API Key?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{color: 'rgba(255, 255, 255, 0.8)'}}>
                        {t('accountPage.apiKeysManager.dialogs.revoke.confirm')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button onClick={closeRevokeDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('accountPage.apiKeysManager.dialogs.create.cancel')}
                    </Button>
                    <Button 
                        onClick={confirmRevokeKey} 
                        color="error" 
                        variant="contained" 
                        disabled={isRevoking}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isRevoking ? <CircularProgress size={24} color="inherit" /> : t('accountPage.apiKeysManager.tooltips.revoke')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiKeysManager;